import {
  buildBarComparisonElement,
  compileBarComparisonSpec,
  validateBarComparisonSpec,
} from "@/lib/ai/visualization/views/bar-comparison/logic";
import { barComparisonAuthoringSchema } from "@/lib/ai/visualization/views/bar-comparison/schema";
import { defineVizView } from "@/lib/ai/visualization/views/shared/view-definition";

/**
 * `bar-comparison` definition の SSOT エントリ。
 */
export const barComparisonDefinition = defineVizView({
  type: "bar-comparison",
  componentType: "BarComparison",
  authoringSchema: barComparisonAuthoringSchema,
  authoringPrompt: {
    summary:
      "Category comparison chart for grouped or stacked bars, including pivoted long-form breakdowns.",
    requiredKeys: [
      "bindingMode",
      "categoryKey",
      "categoryLabel",
      "stacking",
      "sort",
    ],
    optionalKeys: [
      "seriesKeys",
      "seriesLabelEntries",
      "seriesByKey",
      "valueKey",
      "valueLabel",
    ],
    selectionRules: [
      'Use stacking="grouped" unless additive stacked bars are clearly intended.',
      'Use sort="desc" unless the request clearly needs a different setting.',
      "Choose this view for side-by-side category comparison across multiple visible metrics or one pivoted series dimension.",
      'Use bindingMode="wide" for already-wide metrics and provide seriesKeys plus seriesLabelEntries.',
      'Use bindingMode="long" only when one metric must be pivoted by a dimension; then provide seriesByKey, valueKey, and valueLabel.',
      "Always provide categoryLabel in task.outputLanguage.",
      "Always provide series labels only for the metric keys owned by the chosen binding mode.",
    ],
    avoidWhen: [
      "Long time-series trends where the x-axis should stay temporal.",
      "Large rankings with many categories or detailed audit tables.",
      "Grouped long-form datasets that would create too many visible bars at once.",
    ],
    minimalExamples: [
      {
        label: "wide-grouped-bars",
        view: {
          type: "bar-comparison",
          bindingMode: "wide",
          categoryKey: "channel",
          categoryLabel: "Channel",
          stacking: "grouped",
          sort: "desc",
          seriesKeys: ["sessions", "conversions"],
          seriesLabelEntries: [
            { key: "sessions", label: "Sessions" },
            { key: "conversions", label: "Conversions" },
          ],
        },
      },
      {
        label: "long-pivot-bars",
        view: {
          type: "bar-comparison",
          bindingMode: "long",
          categoryKey: "date",
          categoryLabel: "Date",
          stacking: "grouped",
          sort: "desc",
          seriesByKey: "device",
          valueKey: "sessions",
          valueLabel: "Sessions",
        },
      },
    ],
  },
  datasetRequirements: {
    requiresTimeDimension: false,
    requiresCategoryDimension: true,
    minMetricCount: 1,
    requiresCompareMode: false,
  },
  validateSpec: validateBarComparisonSpec,
  buildElement: buildBarComparisonElement,
  compileSpec: compileBarComparisonSpec,
});
